import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ThreeDot } from "react-loading-indicators";
import { StreamChat } from "stream-chat";
import {
  Chat,
  Channel,
  ChannelList,
  Window,
  ChannelHeader,
  MessageList,
  MessageInput,
  Thread,
  Avatar,
} from "stream-chat-react";

import "stream-chat-react/css/v2/index.css";

const STREAM_API_KEY = "fbmvyqg55v45";

function CustomChannelPreview({
  channel,
  setActiveChannel,
  activeChannel,
  currentUserId,
}) {
  const members = Object.values(channel.state.members || {});

  const receiver = members.find((member) => member.user?.id !== currentUserId);

  const receiverName =
    receiver?.user?.name || receiver?.user?.id || "Unknown User";

  const image =
    receiver?.user?.image || channel.data?.image || "/default-avatar.png";

  const lastMessage =
    channel.state.messages?.[channel.state.messages.length - 1]?.text ||
    "لا توجد رسائل بعد";

  const lastAt = channel.state.last_message_at || channel.data?.last_message_at;

  const time = lastAt
    ? new Date(lastAt).toLocaleTimeString("ar-SY", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  const isActive = activeChannel?.id === channel.id;

  return (
    <div
      onClick={() => setActiveChannel(channel)}
      className={`flex items-center gap-3 px-4 py-3 border-b border-black cursor-pointer transition-all ${
        isActive ? "bg-thirdly text-white" : "hover:bg-thirdly text-gray-800"
      }`}
    >
      <Avatar image={image} name={receiverName} size={40} />

      <div className="flex flex-col w-full min-w-0">
        <div className="font-medium text-sm text-white truncate">
          {receiverName}
        </div>

        <div className="text-xs flex justify-between items-center mt-1 text-gray-400 font-medium gap-2">
          <p className="truncate max-w-[120px]">{lastMessage}</p>

          <p className="shrink-0">{time}</p>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const { i18n } = useTranslation();

  const currentLang = i18n.language;

  const [chatClient, setChatClient] = useState(null);
  const [activeChannel, setActiveChannel] = useState(null);
  const [error, setError] = useState("");

  const initialized = useRef(false);

  const USER_ID = localStorage.getItem("stream_user_name");
  const STREAM_TOKEN = localStorage.getItem("stream_token");

  useEffect(() => {
    if (initialized.current) return;

    initialized.current = true;

    const initChat = async () => {
      try {
        if (!USER_ID) {
          setError("stream_user_name not found");
          return;
        }

        if (!STREAM_TOKEN) {
          setError("stream_token not found");
          return;
        }

        console.log("USER_ID:", USER_ID);
        console.log("TOKEN:", STREAM_TOKEN);

        const client = StreamChat.getInstance(STREAM_API_KEY);

        if (!client.userID) {
          await client.connectUser(
            {
              id: USER_ID,
              name: "support",
            },
            STREAM_TOKEN,
          );
        }

        console.log("CONNECTED USER:", client.user);
        console.log("SOCKET HEALTH:", client.wsConnection?.isHealthy);

        setChatClient(client);

        const channels = await client.queryChannels(
          {
            type: "messaging",
            members: { $in: [USER_ID] },
          },
          {
            last_message_at: -1,
          },
          {
            watch: true,
            state: true,
          },
        );

        console.log("CHANNELS:", channels);

        if (channels.length > 0) {
          setActiveChannel(channels[0]);
        }
      } catch (err) {
        console.error("STREAM ERROR:", err);
        setError(err.message || "Connection Failed");
      }
    };

    initChat();
  }, [USER_ID, STREAM_TOKEN]);

  if (error) {
    return (
      <div className="flex justify-center items-center h-[25rem] text-red-500">
        {error}
      </div>
    );
  }

  if (!chatClient) {
    return (
      <div className="flex justify-center items-center h-[25rem]">
        <ThreeDot variant="bounce" color="#D06421" size="large" />
      </div>
    );
  }

  return (
    <div className="py-2 px-4">
      <Chat client={chatClient} theme="str-chat__theme-dark">
        <div className="flex h-[84vh] bg-gray-50 rounded-md overflow-hidden">
          {/* Sidebar */}

          <aside className="w-72 flex flex-col shadow-md">
            <div className="py-[18px] text-center text-white font-semibold bg-black">
              {currentLang === "ar" ? "المحادثات" : "Conversations"}
            </div>

            <div className="flex-1 overflow-y-auto">
              <ChannelList
                filters={{
                  type: "messaging",
                  members: {
                    $in: [USER_ID],
                  },
                }}
                sort={{
                  last_message_at: -1,
                }}
                options={{
                  watch: true,
                  state: true,
                }}
                Preview={(props) => (
                  <CustomChannelPreview
                    {...props}
                    activeChannel={activeChannel}
                    setActiveChannel={setActiveChannel}
                    currentUserId={USER_ID}
                  />
                )}
              />
            </div>
          </aside>

          {/* Chat */}

          <div className="flex flex-1 flex-col">
            {activeChannel ? (
              <Channel channel={activeChannel}>
                <Window>
                  <ChannelHeader />

                  <MessageList />

                  <MessageInput
                    focus
                    placeholder={
                      currentLang === "ar"
                        ? "اكتب رسالتك هنا..."
                        : "Type your message..."
                    }
                  />
                </Window>

                <Thread />
              </Channel>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-black">
                <h3 className="text-white">
                  {currentLang === "ar"
                    ? "👈 اختر محادثة لبدء الدردشة"
                    : "Choose a chat to start chatting"}
                </h3>
              </div>
            )}
          </div>
        </div>
      </Chat>
    </div>
  );
}
